import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import React from 'react';
import { DataRoomTeaser } from '../../shared/models';

interface TeaserActionButtonProps {
	teaser: DataRoomTeaser;
	hasLaunched: boolean;
	onEdit: (teaser: DataRoomTeaser) => void;
	onRespond: (teaser: DataRoomTeaser) => void;
	onSend: (teaser: DataRoomTeaser) => void;
	onRemove: (teaser: DataRoomTeaser) => void;
	onReset: (teaser: DataRoomTeaser) => void;
	onViewDetails: (teaser: DataRoomTeaser) => void;
	onRestore: (teaser: DataRoomTeaser) => void;
	onToggleReferralLanguage: (teaser: DataRoomTeaser) => void;
	onIgnoreLaunch: (teaser: DataRoomTeaser) => void;
	onSendIntroEmail: (teaser: DataRoomTeaser) => void;
	teasersReady: boolean;
	isDirectConnectOrIntroDeal: boolean;
	tab: string;
}

type TeaserActionItem = keyof typeof TeaserActionItemMap;
const TeaserActionItemMap = {
	Tranche: 'Tranche',
	Respond: 'Respond',
	ViewDetails: 'View Details',
	Reset: 'Reset',
	Send: 'Send',
	Resend: 'Resend',
	Remove: 'Remove',
	Restore: 'Restore',
	IncludeLanguage: 'Include Referral Language',
	ExcludeLanguage: 'Exclude Referral Language',
	IgnoreAtLaunch: 'Ignore at Launch',
	IncludeAtLaunch: 'Include at Launch',
	DoNotSendIntroEmail: 'Do Not Send Intro email',
	SendIntroEmail: 'Send Intro email',
};

export const TeaserActionButton: React.FC<TeaserActionButtonProps> = ({
	teaser,
	hasLaunched,
	onEdit,
	onRespond,
	onSend,
	onRemove,
	onReset,
	onViewDetails,
	onRestore,
	teasersReady,
	onToggleReferralLanguage,
	onIgnoreLaunch,
	onSendIntroEmail,
	isDirectConnectOrIntroDeal,
	tab,
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const isPending = teaser.decisionStatus == 'Unsent' || teaser.decisionStatus == 'Sent';
	const isPendingTab = tab == 'unsent' || tab == 'sent';
	const isNotSent = !teaser.sentOn;
	const isNotPending = !isPending;
	const isDeclinedOrDeleted = teaser.isDeleted || teaser.decisionStatus == 'Declined';
	const showIgnoreIncludeOptions = isPendingTab && !hasLaunched && !isDeclinedOrDeleted;
	const showIntroOption = isDirectConnectOrIntroDeal && tab === 'unsent';

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: TeaserActionItem) => {
		switch (action) {
			case 'Tranche':
				onEdit(teaser);
				break;
			case 'ViewDetails':
				onViewDetails(teaser);
				break;
			case 'Send':
			case 'Resend':
				onSend(teaser);
				break;
			case 'Remove':
				onRemove(teaser);
				break;
			case 'Reset':
				onReset(teaser);
				break;
			case 'Respond':
				onRespond(teaser);
				break;
			case 'Restore':
				onRestore(teaser);
				break;
			case 'ExcludeLanguage':
			case 'IncludeLanguage':
				onToggleReferralLanguage(teaser);
				break;
			case 'IgnoreAtLaunch':
			case 'IncludeAtLaunch':
				onIgnoreLaunch(teaser);
				break;
			case 'DoNotSendIntroEmail':
			case 'SendIntroEmail':
				onSendIntroEmail(teaser);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, actionItem: TeaserActionItem) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id="split-button-menu" autoFocusItem>
									{isPending && teasersReady && !teaser.isDeleted && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Respond')}>
											{TeaserActionItemMap['Respond']}
										</MenuItem>
									)}
									{isPending && teasersReady && !teaser.isDeleted && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Send')}>
											{isNotSent ? TeaserActionItemMap['Send'] : TeaserActionItemMap['Resend']}
										</MenuItem>
									)}
									{isPending && !teaser.isDeleted && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Tranche')}>
											{TeaserActionItemMap['Tranche']}
										</MenuItem>
									)}
									{isPending && isNotSent && !teaser.isDeleted && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Remove')}>
											{TeaserActionItemMap['Remove']}
										</MenuItem>
									)}
									{isNotPending && !teaser.isDeleted && !teaser.hasAccessedDataRoom && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Reset')}>
											{TeaserActionItemMap['Reset']}
										</MenuItem>
									)}
									{teaser.includeReferralLanguage && !isDeclinedOrDeleted && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'ExcludeLanguage')}>
											{TeaserActionItemMap['ExcludeLanguage']}
										</MenuItem>
									)}
									{!teaser.includeReferralLanguage &&
										!isDeclinedOrDeleted &&
										!teaser.hasAccessedDataRoom && (
											<MenuItem onClick={event => handleMenuItemClick(event, 'IncludeLanguage')}>
												{TeaserActionItemMap['IncludeLanguage']}
											</MenuItem>
										)}
									{!teaser.ignoreAtLaunch && showIgnoreIncludeOptions && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'IgnoreAtLaunch')}>
											{TeaserActionItemMap['IgnoreAtLaunch']}
										</MenuItem>
									)}
									{teaser.ignoreAtLaunch && showIgnoreIncludeOptions && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'IgnoreAtLaunch')}>
											{TeaserActionItemMap['IncludeAtLaunch']}
										</MenuItem>
									)}
									{showIntroOption && teaser.sendIntroEmail && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'DoNotSendIntroEmail')}>
											{TeaserActionItemMap['DoNotSendIntroEmail']}
										</MenuItem>
									)}
									{showIntroOption && !teaser.sendIntroEmail && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'SendIntroEmail')}>
											{TeaserActionItemMap['SendIntroEmail']}
										</MenuItem>
									)}
									<MenuItem onClick={event => handleMenuItemClick(event, 'ViewDetails')}>
										{TeaserActionItemMap['ViewDetails']}
									</MenuItem>
									{teaser.isDeleted && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Restore')}>
											{TeaserActionItemMap['Restore']}
										</MenuItem>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};
